import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, FileText, Download, Loader2, CheckCircle, XCircle,
  Users, Target, ListChecks, DollarSign, Palette, Clock, Calendar, 
  Briefcase, Sparkles, Eye, Building2, X
} from "lucide-react";

const iconMap = {
  Users: Users,
  Target: Target,
  ListChecks: ListChecks,
  DollarSign: DollarSign,
  Palette: Palette,
  Clock: Clock,
  Calendar: Calendar,
  Briefcase: Briefcase
};

export const CustomReportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [reportTitle, setReportTitle] = useState("Business Strategy Report");
  const [includeDate, setIncludeDate] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await authFetch(`${API}/custom-report/sections`, {  });
      if (response.ok) {
        const data = await response.json();
        setSections(data.sections);
        const withData = data.sections.filter(s => s.has_data).map(s => s.id);
        setSelectedSections(withData);
      }
    } catch (_error) {
      console.error("Error fetching sections:", _error);
      toast.error("Failed to load report sections");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setSelectedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectAll = () => {
    setSelectedSections(sections.map(s => s.id));
  };

  const selectNone = () => {
    setSelectedSections([]);
  };

  const handlePreviewReport = async () => {
    if (selectedSections.length === 0) {
      toast.error("Please select at least one section");
      return;
    }

    setPreviewing(true);
    try {
      const response = await authFetch(`${API}/custom-report/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          sections: selectedSections,
          title: reportTitle,
          include_date: includeDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewPdf(data.pdf_base64);
        setPreviewOpen(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate preview");
      }
    } catch (_error) {
      toast.error("An error occurred while generating preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerateReport = async () => {
    if (selectedSections.length === 0) {
      toast.error("Please select at least one section");
      return;
    }

    setGenerating(true);
    try {
      const response = await authFetch(`${API}/custom-report/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          sections: selectedSections,
          title: reportTitle,
          include_date: includeDate
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Report generated successfully!");
        setPreviewOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate report");
      }
    } catch (_error) {
      toast.error("An error occurred while generating the report");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading report builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100" data-testid="custom-report-page">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#333333] to-[#555555] rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#39FF14]" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-[#333333]">Custom Report Builder</h1>
                  <p className="text-sm text-neutral-500">Create professional business reports</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline"
                onClick={handlePreviewReport} 
                disabled={previewing || selectedSections.length === 0}
                className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
                data-testid="preview-report-btn"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                {previewing ? "Loading..." : "Preview"}
              </Button>
              <Button 
                onClick={handleGenerateReport} 
                disabled={generating || selectedSections.length === 0}
                className="bg-gradient-to-r from-[#333333] to-[#444444] hover:from-[#444444] hover:to-[#555555] text-white shadow-lg"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {generating ? "Generating..." : "Generate PDF"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero Preview Card */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-[#333333] via-[#444444] to-[#333333] text-white mb-8 overflow-hidden">
          <CardContent className="p-0">
            <div className="flex items-center">
              <div className="flex-1 p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-8 w-auto brightness-0 invert" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-[#39FF14]">{reportTitle || "Your Report Title"}</h2>
                <p className="text-neutral-300 mb-4">
                  Prepared for: <span className="text-white font-medium">{user?.name}</span>
                </p>
                <div className="flex items-center space-x-4 text-sm text-neutral-400">
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    {selectedSections.length} sections
                  </span>
                </div>
              </div>
              <div className="hidden md:block p-8">
                <div 
                  className="w-48 h-32 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
                  onClick={selectedSections.length > 0 ? handlePreviewReport : undefined}
                >
                  <div className="text-center">
                    {previewing ? (
                      <Loader2 className="w-8 h-8 text-[#39FF14] mx-auto mb-2 animate-spin" />
                    ) : (
                      <Eye className="w-8 h-8 text-[#39FF14] mx-auto mb-2" />
                    )}
                    <span className="text-xs text-neutral-300">
                      {selectedSections.length > 0 ? "Click to Preview" : "Select sections"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Report Settings */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-neutral-200 shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-[#39FF14]" />
                  Report Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="report-title" className="text-sm font-medium">Report Title</Label>
                  <Input
                    id="report-title"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    placeholder="Enter report title"
                    className="border-neutral-300 focus:border-[#39FF14] focus:ring-[#39FF14]"
                    data-testid="report-title-input"
                  />
                </div>
                
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="include-date" 
                    checked={includeDate} 
                    onCheckedChange={setIncludeDate}
                    className="data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14]"
                  />
                  <Label htmlFor="include-date" className="text-sm cursor-pointer">Include generation date</Label>
                </div>

                <Separator className="my-4" />

                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={selectAll} className="flex-1 text-xs">
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectNone} className="flex-1 text-xs">
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-neutral-200 shadow-md bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="p-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#39FF14]">{selectedSections.length}</div>
                  <div className="text-sm text-neutral-600">Sections Selected</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {sections.filter(s => s.has_data).length} sections have data
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section Selection */}
          <div className="lg:col-span-2">
            <Card className="border-neutral-200 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-[#333333]" />
                  Report Sections
                </CardTitle>
                <CardDescription>
                  Choose which sections to include in your professional report
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {sections.map((section) => {
                    const Icon = iconMap[section.icon] || FileText;
                    const isSelected = selectedSections.includes(section.id);
                    
                    return (
                      <div
                        key={section.id}
                        className={`relative flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? "border-[#39FF14] bg-gradient-to-br from-green-50 to-emerald-50 shadow-md" 
                            : "border-neutral-200 hover:border-neutral-300 hover:shadow-sm bg-white"
                        } ${!section.has_data ? "opacity-70" : ""}`}
                        onClick={() => toggleSection(section.id)}
                        data-testid={`section-${section.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSection(section.id)}
                          className="data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14]"
                        />
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                          isSelected 
                            ? "bg-[#39FF14] shadow-lg" 
                            : "bg-neutral-100"
                        }`}>
                          <Icon className={`w-6 h-6 ${isSelected ? "text-white" : "text-neutral-500"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${isSelected ? "text-[#333333]" : "text-neutral-700"}`}>
                            {section.name}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            {section.has_data ? (
                              <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-50">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Ready
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                                <XCircle className="w-3 h-3 mr-1" />
                                No Data
                              </Badge>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Info Box */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Professional PDF Reports</h3>
                <p className="text-sm text-blue-700">
                  Your report will be generated with the My Biz Consulting branding, including logo, 
                  professional formatting, and organized sections. Use the Preview button to see how 
                  your report will look before downloading.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg">Report Preview</DialogTitle>
                <DialogDescription>
                  Preview your report before downloading
                </DialogDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="bg-[#39FF14] hover:bg-[#32E010] text-white"
                  data-testid="download-from-preview-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {generating ? "Downloading..." : "Download PDF"}
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden p-4">
            {previewPdf ? (
              <iframe
                src={`data:application/pdf;base64,${previewPdf}`}
                className="w-full h-full rounded-lg border border-neutral-200"
                title="PDF Preview"
                data-testid="pdf-preview-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-neutral-100 rounded-lg">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomReportPage;
